import React, { useState, useEffect } from 'react'
import {
  XMarkIcon,
  CheckIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { adminPlanApi } from '../../../shared/services/adminPlanApi'
import { Plan } from '../types/plan.types'

interface PlanEditorProps {
  isOpen: boolean
  onClose: () => void
  plan?: Plan | null
  onSave: (plan: Plan) => void
}

const PLAN_TYPES = [
  { value: 'free', label: 'Free', color: '#6b7280' },
  { value: 'growth', label: 'Growth', color: '#3b82f6' },
  { value: 'scale', label: 'Scale', color: '#8b5cf6' },
  { value: 'enterprise', label: 'Enterprise', color: '#059669' },
]

const FEATURE_DESCRIPTIONS = {
  linksPerMonth: 'Number of links that can be created per month',
  clicksPerMonth: 'Total clicks allowed per month across all links',
  customDomains: 'Number of custom domains that can be connected',
  teamMembers: 'Number of team members that can access the account',
  analytics: 'Advanced analytics and reporting features',
  apiAccess: 'REST API access for integrations',
  qrCodes: 'QR code generation for all links',
  passwordProtection: 'Password protection for sensitive links',
  expirationDates: 'Set expiration dates for links',
  customSlug: 'Custom short link aliases',
  whiteLabel: 'Remove ShortURL branding',
  bulkOperations: 'Bulk link creation and management',
  advancedSecurity: 'Advanced security features and threat protection',
  prioritySupport: '24/7 priority customer support',
  sslCertificates: 'SSL certificates for custom domains',
  webhooks: 'Webhook notifications for events',
}

export const PlanEditor: React.FC<PlanEditorProps> = ({
  isOpen,
  onClose,
  plan,
  onSave,
}) => {
  const [formData, setFormData] = useState<Plan>({
    name: '',
    type: 'free',
    displayName: '',
    description: '',
    price: {
      monthly: 0,
      yearly: 0,
      yearlyDiscount: 0,
    },
    features: {
      linksPerMonth: 50,
      clicksPerMonth: 1000,
      customDomains: 0,
      teamMembers: 1,
      analytics: false,
      apiAccess: false,
      qrCodes: true,
      passwordProtection: false,
      expirationDates: false,
      customSlug: false,
      whiteLabel: false,
      bulkOperations: false,
      advancedSecurity: false,
      prioritySupport: false,
      sslCertificates: false,
      webhooks: false,
    },
    popular: false,
    badge: '',
    color: '#6b7280',
    isActive: true,
    isPublic: true,
  })

  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (plan) {
      setFormData(plan)
    } else {
      // Reset to default values when creating new plan
      setFormData({
        name: '',
        type: 'free',
        displayName: '',
        description: '',
        price: {
          monthly: 0,
          yearly: 0,
          yearlyDiscount: 0,
        },
        features: {
          linksPerMonth: 50,
          clicksPerMonth: 1000,
          customDomains: 0,
          teamMembers: 1,
          analytics: false,
          apiAccess: false,
          qrCodes: true,
          passwordProtection: false,
          expirationDates: false,
          customSlug: false,
          whiteLabel: false,
          bulkOperations: false,
          advancedSecurity: false,
          prioritySupport: false,
          sslCertificates: false,
          webhooks: false,
        },
        popular: false,
        badge: '',
        color: '#6b7280',
        isActive: true,
        isPublic: true,
      })
    }
    setErrors({})
  }, [plan, isOpen])

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Plan name is required'
    }

    if (!formData.displayName.trim()) {
      newErrors.displayName = 'Display name is required'
    }

    if (formData.price.monthly < 0) {
      newErrors.monthlyPrice = 'Monthly price cannot be negative'
    }

    if (formData.price.yearly < 0) {
      newErrors.yearlyPrice = 'Yearly price cannot be negative'
    }

    if (
      formData.price.yearlyDiscount &&
      (formData.price.yearlyDiscount < 0 || formData.price.yearlyDiscount > 100)
    ) {
      newErrors.yearlyDiscount = 'Yearly discount must be between 0 and 100'
    }

    // Validate numeric features
    if (formData.features.linksPerMonth < -1) {
      newErrors.linksPerMonth =
        'Links per month must be -1 (unlimited) or positive'
    }

    if (formData.features.clicksPerMonth < -1) {
      newErrors.clicksPerMonth =
        'Clicks per month must be -1 (unlimited) or positive'
    }

    if (formData.features.customDomains < -1) {
      newErrors.customDomains =
        'Custom domains must be -1 (unlimited) or positive'
    }

    if (formData.features.teamMembers < -1) {
      newErrors.teamMembers = 'Team members must be -1 (unlimited) or positive'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      toast.error('Please fix the validation errors')
      return
    }

    setLoading(true)
    try {
      // 예전에는 인증 헤더 없이 저장했다. 요금제 라우트에 인증이 걸린 뒤로
      // 401 이 나던 자리다.
      const saved = plan?._id
        ? await adminPlanApi.update(plan._id, formData)
        : await adminPlanApi.create(formData)

      toast.success(
        plan ? 'Plan updated successfully' : 'Plan created successfully'
      )
      onSave(saved)
      onClose()
    } catch (error: any) {
      toast.error(error?.message || 'Failed to save plan')
      console.error('Plan save error:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleFeatureChange = (
    feature: keyof Plan['features'],
    value: boolean | number
  ) => {
    setFormData(prev => ({
      ...prev,
      features: {
        ...prev.features,
        [feature]: value,
      },
    }))
  }

  const formatNumberValue = (value: number): string => {
    return value === -1 ? 'Unlimited' : value.toLocaleString()
  }

  const calculateYearlySavings = (): number => {
    if (formData.price.monthly === 0) {
      return 0
    }
    const monthlyTotal = formData.price.monthly * 12
    return monthlyTotal - formData.price.yearly
  }

  const calculateYearlySavingsPercent = (): number => {
    if (formData.price.monthly === 0) {
      return 0
    }
    const monthlyTotal = formData.price.monthly * 12
    return Math.round(
      ((monthlyTotal - formData.price.yearly) / monthlyTotal) * 100
    )
  }

  if (!isOpen) {
    return null
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-lg bg-white dark:bg-gray-800">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 p-6 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {plan ? 'Edit Plan' : 'Create New Plan'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8 p-6">
          {/* Basic Information */}
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Basic Information
            </h3>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Plan Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={e =>
                    setFormData(prev => ({ ...prev, name: e.target.value }))
                  }
                  className="w-full rounded-md border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  placeholder="e.g. growth"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-500">{errors.name}</p>
                )}
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Plan Type *
                </label>
                <select
                  value={formData.type}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      type: e.target.value as Plan['type'],
                      color:
                        PLAN_TYPES.find(t => t.value === e.target.value)
                          ?.color || '#6b7280',
                    }))
                  }
                  className="w-full rounded-md border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                >
                  {PLAN_TYPES.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Display Name *
                </label>
                <input
                  type="text"
                  value={formData.displayName}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      displayName: e.target.value,
                    }))
                  }
                  className="w-full rounded-md border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  placeholder="e.g. Growth Plan"
                />
                {errors.displayName && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.displayName}
                  </p>
                )}
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Badge (Optional)
                </label>
                <input
                  type="text"
                  value={formData.badge || ''}
                  onChange={e =>
                    setFormData(prev => ({ ...prev, badge: e.target.value }))
                  }
                  className="w-full rounded-md border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  placeholder="e.g. Most Popular"
                />
              </div>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Description
              </label>
              <textarea
                value={formData.description || ''}
                onChange={e =>
                  setFormData(prev => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                rows={3}
                className="w-full rounded-md border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                placeholder="Brief description of this plan..."
              />
            </div>

            <div className="flex items-center space-x-6">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.popular || false}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      popular: e.target.checked,
                    }))
                  }
                  className="rounded border-gray-300 text-blue-600"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  Mark as Popular
                </span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      isActive: e.target.checked,
                    }))
                  }
                  className="rounded border-gray-300 text-blue-600"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  Active
                </span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.isPublic}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      isPublic: e.target.checked,
                    }))
                  }
                  className="rounded border-gray-300 text-blue-600"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  Public
                </span>
              </label>
            </div>
          </div>

          {/* Pricing */}
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Pricing
            </h3>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Monthly Price (₩)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.price.monthly}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      price: {
                        ...prev.price,
                        monthly: parseFloat(e.target.value) || 0,
                      },
                    }))
                  }
                  className="w-full rounded-md border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
                {errors.monthlyPrice && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.monthlyPrice}
                  </p>
                )}
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Yearly Price (₩)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.price.yearly}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      price: {
                        ...prev.price,
                        yearly: parseFloat(e.target.value) || 0,
                      },
                    }))
                  }
                  className="w-full rounded-md border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
                {errors.yearlyPrice && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.yearlyPrice}
                  </p>
                )}
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Yearly Discount (%)
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={formData.price.yearlyDiscount || 0}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      price: {
                        ...prev.price,
                        yearlyDiscount: parseInt(e.target.value) || 0,
                      },
                    }))
                  }
                  className="w-full rounded-md border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
                {errors.yearlyDiscount && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.yearlyDiscount}
                  </p>
                )}
              </div>
            </div>

            {/* Pricing Preview */}
            {formData.price.monthly > 0 && (
              <div className="rounded-lg bg-blue-50 p-4 dark:bg-blue-900/20">
                <div className="mb-2 flex items-center">
                  <InformationCircleIcon className="mr-2 h-5 w-5 text-blue-500" />
                  <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
                    Pricing Preview
                  </span>
                </div>
                <div className="text-sm text-blue-700 dark:text-blue-300">
                  <p>Monthly: ₩{formData.price.monthly}/month</p>
                  <p>
                    Yearly: ₩{formData.price.yearly}/year (₩
                    {(formData.price.yearly / 12).toFixed(2)}/month)
                  </p>
                  {calculateYearlySavings() > 0 && (
                    <p className="font-medium text-green-600 dark:text-green-400">
                      Yearly saves: ₩{calculateYearlySavings()} (
                      {calculateYearlySavingsPercent()}% off)
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Limits & Features */}
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Limits & Features
            </h3>

            {/* Numeric Limits */}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Links per Month
                  <span className="ml-1 text-xs text-gray-500">
                    (Use -1 for unlimited)
                  </span>
                </label>
                <input
                  type="number"
                  min="-1"
                  value={formData.features.linksPerMonth}
                  onChange={e =>
                    handleFeatureChange(
                      'linksPerMonth',
                      parseInt(e.target.value) || 0
                    )
                  }
                  className="w-full rounded-md border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Display: {formatNumberValue(formData.features.linksPerMonth)}
                </p>
                {errors.linksPerMonth && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.linksPerMonth}
                  </p>
                )}
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Clicks per Month
                  <span className="ml-1 text-xs text-gray-500">
                    (Use -1 for unlimited)
                  </span>
                </label>
                <input
                  type="number"
                  min="-1"
                  value={formData.features.clicksPerMonth}
                  onChange={e =>
                    handleFeatureChange(
                      'clicksPerMonth',
                      parseInt(e.target.value) || 0
                    )
                  }
                  className="w-full rounded-md border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Display: {formatNumberValue(formData.features.clicksPerMonth)}
                </p>
                {errors.clicksPerMonth && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.clicksPerMonth}
                  </p>
                )}
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Custom Domains
                  <span className="ml-1 text-xs text-gray-500">
                    (Use -1 for unlimited)
                  </span>
                </label>
                <input
                  type="number"
                  min="-1"
                  value={formData.features.customDomains}
                  onChange={e =>
                    handleFeatureChange(
                      'customDomains',
                      parseInt(e.target.value) || 0
                    )
                  }
                  className="w-full rounded-md border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Display: {formatNumberValue(formData.features.customDomains)}
                </p>
                {errors.customDomains && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.customDomains}
                  </p>
                )}
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Team Members
                  <span className="ml-1 text-xs text-gray-500">
                    (Use -1 for unlimited)
                  </span>
                </label>
                <input
                  type="number"
                  min="-1"
                  value={formData.features.teamMembers}
                  onChange={e =>
                    handleFeatureChange(
                      'teamMembers',
                      parseInt(e.target.value) || 0
                    )
                  }
                  className="w-full rounded-md border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Display: {formatNumberValue(formData.features.teamMembers)}
                </p>
                {errors.teamMembers && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.teamMembers}
                  </p>
                )}
              </div>
            </div>

            {/* Boolean Features */}
            <div>
              <h4 className="mb-4 text-sm font-medium text-gray-700 dark:text-gray-300">
                Features
              </h4>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                {Object.entries(FEATURE_DESCRIPTIONS).map(
                  ([feature, description]) => {
                    if (
                      typeof formData.features[
                        feature as keyof Plan['features']
                      ] !== 'boolean'
                    ) {
                      return null
                    }

                    return (
                      <label
                        key={feature}
                        className="flex items-start space-x-3 rounded-lg border border-gray-200 p-3 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700"
                      >
                        <input
                          type="checkbox"
                          checked={
                            formData.features[
                              feature as keyof Plan['features']
                            ] as boolean
                          }
                          onChange={e =>
                            handleFeatureChange(
                              feature as keyof Plan['features'],
                              e.target.checked
                            )
                          }
                          className="mt-1 rounded border-gray-300 text-blue-600"
                        />
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {feature
                              .replace(/([A-Z])/g, ' $1')
                              .replace(/^./, str => str.toUpperCase())}
                          </div>
                          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            {description}
                          </div>
                        </div>
                      </label>
                    )
                  }
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end space-x-4 border-t border-gray-200 pt-6 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center rounded-lg bg-blue-600 px-6 py-2 text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                  Saving...
                </>
              ) : (
                <>
                  <CheckIcon className="mr-2 h-4 w-4" />
                  {plan ? 'Update Plan' : 'Create Plan'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default PlanEditor
